import type { EChartsOption } from 'echarts';

import { COLOR, FONT } from '@/shared/design-tokens/design-tokens';
import { formatMetricValue } from '@/shared/lib/utils/number';

import type { TimeSeriesData } from '@/features/monitoring/api/types';

type GpuMetricData = {
  displayName: string;
  data: TimeSeriesData[];
};

/**
 * GPU 라인 차트 ECharts 옵션 생성
 * @param data - GPU 메트릭 데이터 배열
 * @param colors - 차트 색상 팔레트
 * @returns ECharts 옵션 객체
 */
export function createGpuLineChartOption(data: GpuMetricData[], colors: readonly string[]): EChartsOption {
  if (!data || data.length === 0) {
    return {};
  }

  const series = data.map((metric, index) => ({
    name: metric.displayName,
    type: 'line' as const,
    data: metric.data.map((d) => [d.timestamp, d.value]),
    smooth: false,
    symbol: 'none',
    lineStyle: {
      width: 2,
      color: colors[index % colors.length],
    },
    itemStyle: {
      color: colors[index % colors.length],
    },
  }));

  return {
    backgroundColor: 'transparent',
    grid: {
      left: 60,
      right: 40,
      top: 40,
      bottom: 60,
      containLabel: false,
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: COLOR.GRAY[10],
      borderColor: COLOR.GRAY[30],
      borderWidth: 1,
      textStyle: {
        color: COLOR.GRAY[100],
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontSize: FONT.SIZE.SM,
      },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return '';

        type TooltipParam = {
          color: string;
          seriesName: string;
          value: [number, number];
        };

        const typedParams = params as TooltipParam[];
        const timestamp = new Date(typedParams[0].value[0]).toLocaleTimeString();
        const lines = typedParams.map(
          (param) =>
            `<div style="display: flex; align-items: center; gap: 8px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${param.color};"></span>
              <span>${param.seriesName}: ${formatMetricValue(param.value[1])}%</span>
            </div>`
        );
        return `<div><div style="margin-bottom: 4px;">${timestamp}</div>${lines.join('')}</div>`;
      },
    },
    xAxis: {
      type: 'time',
      axisLine: {
        lineStyle: {
          color: COLOR.GRAY[70],
        },
      },
      axisLabel: {
        color: COLOR.GRAY[60],
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontSize: FONT.SIZE.XS,
        formatter: (value: number) => {
          const date = new Date(value);
          return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        },
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: COLOR.GRAY[90],
          type: 'dashed',
        },
      },
    },
    yAxis: {
      type: 'value',
      name: '%',
      min: 0,
      max: 100,
      nameTextStyle: {
        color: COLOR.GRAY[60],
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontSize: FONT.SIZE.XS,
      },
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: COLOR.GRAY[60],
        fontFamily: FONT.FAMILY.NANUMSQUARE,
        fontSize: FONT.SIZE.XS,
        formatter: (value: number) => `${value}%`,
      },
      splitLine: {
        lineStyle: {
          color: COLOR.GRAY[90],
          type: 'dashed',
        },
      },
    },
    legend: {
      show: false,
    },
    series,
  };
}
